import {
  Avatar,
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { RecentCompany, useRecentCompanies } from '@/services/recent-companies';

/**
 * "RECIENTES" card with the most recently searched companies across
 * sessions. Reads from `useRecentCompanies` (localStorage-backed, capped
 * at 5 entries). Clicking an item navigates to `/company/:companyId`.
 * Renders nothing when the recent list is empty (no empty card).
 */
export const RecentCompaniesCard = () => {
  const { recent } = useRecentCompanies();
  const navigate = useNavigate();

  if (recent.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ letterSpacing: '0.08em' }}
        >
          RECIENTES
        </Typography>
        <List dense disablePadding sx={{ mt: 1 }}>
          {recent.map((company) => (
            <RecentItem
              key={company.companyId}
              company={company}
              onClick={() =>
                navigate(`/company/${encodeURIComponent(company.companyId)}`)
              }
            />
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

interface RecentItemProps {
  company: RecentCompany;
  onClick: () => void;
}

const RecentItem = ({ company, onClick }: RecentItemProps) => (
  <ListItemButton onClick={onClick} sx={{ borderRadius: 2, px: 1 }}>
    <Avatar
      sx={{
        width: 32,
        height: 32,
        mr: 2,
        bgcolor: 'primary.light',
        color: 'primary.dark',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {company.name.charAt(0).toUpperCase()}
    </Avatar>
    <ListItemText
      primary={company.name}
      secondary={company.companyId}
      primaryTypographyProps={{ fontWeight: 600 }}
      secondaryTypographyProps={{
        sx: { fontFamily: 'ui-monospace, monospace', fontSize: 12 },
      }}
    />
    <StatusChip status={company.subscriptionStatus} />
  </ListItemButton>
);

const StatusChip = ({
  status,
}: {
  status: RecentCompany['subscriptionStatus'];
}) => {
  if (status === 'active') {
    return <Chip size="small" color="success" label="Activa" />;
  }
  if (status === 'inactive') {
    return <Chip size="small" label="Inactiva" />;
  }
  if (status === 'migration-pending') {
    // Amber/orange — distinct from the soft-red "Sin suscripción".
    return (
      <Chip
        size="small"
        label="Migración pendiente"
        sx={{ bgcolor: '#FFE9C7', color: '#8A5300' }}
      />
    );
  }
  // Soft red (the previous solid `color="error"` was too strong); reuse the
  // theme's soft warning style.
  return <Chip size="small" color="warning" label="Sin suscripción" />;
};
